import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { AccountSummary, TrendDirection } from '@/types/overview';
import { sortAccounts, formatRelativeDay, type SortKey } from '@/lib/overview';
import { SentimentBar } from '../SentimentBar';

/**
 * One row per account: name + product, sentiment split, avg rating, response
 * count, in-window trend (delta + direction), and last-active. Sortable by
 * volume / rating / trend. Clicking a row drills into that account's dashboard.
 */
export function AccountsTable({
  accounts,
  onSelectAccount,
}: {
  accounts: AccountSummary[];
  onSelectAccount: (accountToken: string) => void;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('volume');

  const rows = useMemo(() => sortAccounts(accounts, sortKey, 'desc'), [accounts, sortKey]);

  return (
    <Card data-testid="overview-accounts-table">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Accounts</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">No accounts to show</p>
        ) : (
          <div>
            {/* Header / sort controls */}
            <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_auto_auto_auto_auto] items-center gap-x-4 border-b pb-2 text-xs font-medium text-muted-foreground">
              <span>Account</span>
              <span>Sentiment</span>
              <SortHeader label="Rating" active={sortKey === 'rating'} onClick={() => setSortKey('rating')} />
              <SortHeader label="Responses" active={sortKey === 'volume'} onClick={() => setSortKey('volume')} />
              <SortHeader label="Trend" active={sortKey === 'trend'} onClick={() => setSortKey('trend')} />
              <span className="justify-self-end">Active</span>
            </div>
            <div className="divide-y">
              {rows.map((a) => (
                <AccountRow key={a.accountToken} account={a} onSelect={onSelectAccount} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SortHeader({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`justify-self-end rounded hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active ? 'text-foreground underline' : ''
      }`}
    >
      {label}
    </button>
  );
}

function AccountRow({ account, onSelect }: { account: AccountSummary; onSelect: (token: string) => void }) {
  const { up, down } = account.votes;
  return (
    <button
      type="button"
      onClick={() => onSelect(account.accountToken)}
      title={`Open ${account.name}`}
      className="grid w-full grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_auto_auto_auto_auto] items-center gap-x-4 py-2.5 text-left hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{account.name}</div>
        <div className="truncate text-xs text-muted-foreground">{account.productName}</div>
      </div>
      <SentimentBar up={up} down={down} total={account.totalFeedback} />
      <div className="justify-self-end text-sm tabular-nums text-warning-500">
        ★ {account.averageRating.toFixed(1)}
      </div>
      <div className="justify-self-end text-sm tabular-nums text-muted-foreground">
        {account.totalFeedback.toLocaleString()}
      </div>
      <div className="justify-self-end">
        <TrendPill direction={account.trendDirection} delta={account.ratingDelta} />
      </div>
      <div className="justify-self-end text-xs text-muted-foreground">
        {formatRelativeDay(account.lastFeedbackAt)}
      </div>
    </button>
  );
}

function TrendPill({ direction, delta }: { direction: TrendDirection; delta: number | null }) {
  if (delta === null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const map = {
    up: { Icon: ArrowUp, cls: 'text-success-600' },
    down: { Icon: ArrowDown, cls: 'text-error-600' },
    steady: { Icon: Minus, cls: 'text-muted-foreground' },
  } as const;
  const { Icon, cls } = map[direction];
  return (
    <span className={`flex items-center gap-0.5 text-xs font-medium tabular-nums ${cls}`}>
      <Icon className="h-3 w-3" />
      {delta > 0 ? '+' : ''}
      {delta.toFixed(1)}
    </span>
  );
}
